import React from "react";
import { useTranslation } from "react-i18next";
import { LogOut, Bell, Search, Sparkles, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";

// Tenants that should display the elegant "DEMO" badge in the topbar.
const DEMO_TENANT_SLUGS = new Set([
  "bella-napoli",
  "hotel-aurora",
  "grand-resort-mare",
  "osteria-mediterraneo",
]);

export default function Topbar({ onMenu }) {
  const { t } = useTranslation();
  const { user, tenant, isPlatform, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "U")
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const isDemo = !isPlatform && tenant?.slug && DEMO_TENANT_SLUGS.has(tenant.slug);

  return (
    <header
      className="h-14 sm:h-16 border-b border-[#E2E8F0] bg-white/70 backdrop-blur-xl sticky top-0 z-30 flex items-center px-3 sm:px-6 gap-2 sm:gap-3"
      data-testid="topbar"
    >
      <button
        type="button"
        onClick={onMenu}
        className="lg:hidden grid place-items-center w-9 h-9 rounded-lg border border-[#D3D7D5] hover:bg-[#F2F4F2] active:bg-[#F1F5F9] shrink-0"
        data-testid="topbar-menu-btn"
        aria-label={t("topbar.menu_aria")}
      >
        <span className="block w-4 h-0.5 bg-[#0f172a] relative before:content-[''] before:absolute before:-top-1.5 before:left-0 before:right-0 before:h-0.5 before:bg-[#0f172a] after:content-[''] after:absolute after:top-1.5 after:left-0 after:right-0 after:h-0.5 after:bg-[#0f172a]" />
      </button>

      <div className="hidden md:flex items-center gap-2 px-3 h-10 rounded-lg border border-[#D3D7D5] bg-[#F7F7F5] w-full max-w-md">
        <Search className="w-4 h-4 text-[#475569]" />
        <input
          data-testid="topbar-search"
          type="text"
          placeholder={
            isPlatform
              ? t("topbar.search_placeholder_platform")
              : t("topbar.search_placeholder_tenant")
          }
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#94A3B8] text-[#0f172a]"
        />
        <kbd className="hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-[#D3D7D5] text-[10px] uppercase tracking-widest text-[#475569]">
          ⌘ K
        </kbd>
      </div>

      {/* Mobile-only live tenant chip — replaces the search bar to fill the empty space */}
      <div
        className="md:hidden flex-1 min-w-0 flex items-center gap-2"
        data-testid="topbar-tenant-mobile"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#102A43] animate-pulse shrink-0" />
        <span className="text-xs font-semibold text-[#0f172a] truncate">
          {tenant?.business_name || t("topbar.platform_label")}
        </span>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-3 ml-auto shrink-0">
        {/* Iter 79 Fase 5.0.1 — Platform mode chip (persistent, low-noise) */}
        {isPlatform && (
          <span
            data-testid="topbar-platform-mode-chip"
            title="Stai usando CostControlX come proprietario della piattaforma"
            className="hidden sm:inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-gradient-to-r from-[#F5F3FF] to-[#EDE9FE] border border-[#C4B5FD] text-[#7C3AED] text-[10px] font-bold uppercase tracking-widest"
          >
            <Crown className="w-3 h-3" />
            Modalità piattaforma
          </span>
        )}
        {isDemo && (
          <span
            title={t("restaurant.dashboard.demo_tooltip")}
            data-testid="topbar-demo-badge"
            className="hidden md:inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-gradient-to-r from-[#FFF3DD] to-[#FDE8C4] border border-[#F1DDA8] text-[#A36B0F] text-[10px] font-semibold uppercase tracking-widest"
          >
            <Sparkles className="w-3 h-3" />
            {t("restaurant.dashboard.demo_badge")}
          </span>
        )}
        <LanguageSwitcher />

        <button
          type="button"
          className="hidden sm:grid place-items-center w-9 h-9 rounded-lg border border-[#D3D7D5] hover:bg-[#F2F4F2] active:bg-[#F1F5F9]"
          data-testid="topbar-notifications"
          aria-label={t("topbar.notifications_aria")}
        >
          <Bell className="w-4 h-4 text-[#475569]" />
        </button>

        <div
          className="hidden lg:flex flex-col items-end leading-tight"
          data-testid="topbar-user"
        >
          <span className="text-sm font-medium text-[#0f172a]">{user?.name}</span>
          <span className="text-[11px] text-[#475569]">
            {tenant?.business_name || t("topbar.platform_label")}
          </span>
        </div>

        <div
          className="grid place-items-center w-9 h-9 rounded-full bg-[#102A43] text-white text-sm font-semibold shrink-0"
          aria-hidden
        >
          {initials}
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="grid place-items-center w-9 h-9 rounded-lg border border-[#D3D7D5] hover:bg-[#F2F4F2] active:bg-[#F1F5F9] hover:text-[#B91C1C] transition-colors"
          data-testid="topbar-logout"
          aria-label={t("topbar.logout_aria")}
          title={t("topbar.logout_aria")}
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </header>
  );
}
